import { z } from 'zod';
import { baseClaimSchema } from './baseClaim';
import { tariffSchema } from './tariff';
import { debitSchema } from './debit';
import { submissionSchema } from './submission';
import {
  claimRelationshipSideSchema,
  claimRelationshipTypeSchema,
} from './claimRelationship';

const claimHistoryChangeMetadataSchema = z.object({
  changedBy: z.string(),
  updatedAt: z.string().nullable(),
});

export const claimHistoryActivitySchema = claimHistoryChangeMetadataSchema
  .extend({
    type: z.literal('activity'),
    field: z.literal('activity'),
    activity: z.enum([
      'tariffCommentAdded',
      'internalCommentAdded',
      'internalCommentRemoved',
      'claimQueried',
      'supportingDocumentsSent',
      'submissionCancelled',
      'claimLinked',
      'claimUnlinked',
    ]),
    commentID: z.guid().optional(),
    commentText: z.string().optional(),
    tariffIDs: z.array(z.guid()).optional(),
    tariffLabels: z.array(z.string()).optional(),
    claimQueryID: z.guid().optional(),
    attachmentIDs: z.array(z.guid()).optional(),
    attachmentFilenames: z.array(z.string()).optional(),
    submissionID: z.guid().optional(),
    submissionFilename: z.string().optional(),
    claimRelationshipID: z.guid().optional(),
    relatedClaimID: z.guid().optional(),
    relatedClaimNumber: z.string().nullable().optional(),
    relationshipType: claimRelationshipTypeSchema.optional(),
    claimSide: claimRelationshipSideSchema.optional(),
  })
  .strict();

const legacyClaimHistoryChangeSchema = claimHistoryChangeMetadataSchema
  .extend({
    type: z.enum(['primitive', 'relation', 'array']).optional(),
    field: z.string().optional(),
    before: z.unknown().nullable().optional(),
    after: z.unknown().nullable().optional(),
    added: z.array(z.unknown()).optional(),
    removed: z.array(z.unknown()).optional(),
    modified: z.array(z.unknown()).optional(),
    differences: z.array(z.unknown()).optional(),
  })
  .passthrough();

export const claimHistoriesSchema = baseClaimSchema.extend({
  changes: z.array(
    z.union([claimHistoryActivitySchema, legacyClaimHistoryChangeSchema])
  ),
  claimID: z.string().uuid(),
  updatedAt: z.date(),
  claim: z.lazy(() => baseClaimSchema.partial()).optional(),
  tariffs: z
    .lazy(() => z.array(tariffSchema.partial()))
    .optional()
    .nullable(),
  debits: z.lazy(() => z.array(debitSchema).optional().nullable()),
  submissions: z.lazy(() => z.array(submissionSchema).optional().nullable()),
});

export type ClaimHistoryActivity = z.infer<typeof claimHistoryActivitySchema>;
